import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Typography,
  Upload,
} from "antd";
import {
  EXPORT_CNF_BILL_EDIT_ENDPOINT,
  EXPORT_CNF_BILL_NEXT_BILL_NO_ENDPOINT,
  EXPORT_CNF_BILL_STORE_ENDPOINT,
  EXPORT_CNF_EXPENDITURE_LIST_ENDPOINT,
  EXPORT_DETAIL_CNF_LIST,
  SALES_CONTRACT_LIST,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import ExpenditureParticularsTable from "components/ImportCustomsClearance/ExternalCnfBills/ExpenditureParticularsTable";
import { computeExportAgencyCommissionTaka } from "components/ImportCustomsClearance/ExternalCnfBills/agencyCommission";
import debounce from "lodash/debounce";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

function buildExpenditureOptions(rows) {
  return (rows || []).map((e) => ({
    value: e.id,
    label: e.name || `ID ${e.id}`,
  }));
}

const savedOrInvoiceDate = (saved, fromInvoice) => {
  const value = saved || fromInvoice;
  return value ? moment(value) : null;
};

const formatDisplayDate = (value) =>
  value && moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : "";

export default function ExportCnfBillForm({
  form,
  isEdit,
  editInfo,
  viewMode,
  billId,
}) {
  const navigate = useNavigate();
  const [exportInvoiceOptions, setExportInvoiceOptions] = useState([]);
  const [salesContractOptions, setSalesContractOptions] = useState([]);
  const [expenditureRows, setExpenditureRows] = useState([]);
  const [loadingInvSearch, setLoadingInvSearch] = useState(false);
  const [loadingScSearch, setLoadingScSearch] = useState(false);
  const [loadingExp, setLoadingExp] = useState(false);
  const [selectedExport, setSelectedExport] = useState(null);
  const [exportInvoiceSelectSearch, setExportInvoiceSelectSearch] =
    useState(undefined);
  const [documentFileList, setDocumentFileList] = useState([]);

  const invoiceNumber = Form.useWatch("invoice_number", form);
  const conversionRate = Form.useWatch("conversion_rate", form);

  const searchExportInvoices = useCallback(async (searchText) => {
    setLoadingInvSearch(true);
    const body = {
      page: 1,
      per_page: 50,
      exclude_used_in_cnf_bill: 1,
      ...(isEdit && billId ? { cnf_bill_id: billId } : {}),
      ...(searchText && String(searchText).trim()
        ? { invoice_number: String(searchText).trim() }
        : {}),
    };
    const res = await getData(EXPORT_DETAIL_CNF_LIST, false, body);
    if (res?.status === 200) {
      const rows = res?.data?.data?.data || res?.data?.data || [];
      const list = Array.isArray(rows) ? rows : [];
      setExportInvoiceOptions(
        list.map((inv) => ({
          value: inv.id,
          label: inv.invoice_number || `Invoice #${inv.id}`,
          invoice: inv,
        })),
      );
    } else {
      setExportInvoiceOptions([]);
    }
    setLoadingInvSearch(false);
  }, [isEdit, billId]);

  const searchExportInvoicesRef = useRef(searchExportInvoices);
  searchExportInvoicesRef.current = searchExportInvoices;
  const debouncedExportSearch = useMemo(
    () =>
      debounce((t) => {
        searchExportInvoicesRef.current(t);
      }, 300),
    [],
  );

  const searchSalesContracts = useCallback(async (searchText) => {
    setLoadingScSearch(true);
    const body = {
      page: 1,
      per_page: 50,
      ...(searchText && String(searchText).trim()
        ? { reference_no: String(searchText).trim() }
        : {}),
    };
    const res = await getData(SALES_CONTRACT_LIST, false, body);
    if (res?.status === 200) {
      const rows = res?.data?.data?.data || [];
      setSalesContractOptions(
        (Array.isArray(rows) ? rows : []).map((sc) => ({
          value: sc.id,
          label: sc.reference_no || sc.sales_contract_unique_id || `SC ${sc.id}`,
        })),
      );
    } else {
      setSalesContractOptions([]);
    }
    setLoadingScSearch(false);
  }, []);

  const debouncedScSearch = useMemo(
    () => debounce((t) => searchSalesContracts(t), 400),
    [searchSalesContracts],
  );

  const fetchExpenditureSettings = useCallback(async () => {
    setLoadingExp(true);
    const res = await getData(EXPORT_CNF_EXPENDITURE_LIST_ENDPOINT, false, {
      per_page: 500,
      page: 1,
    });
    if (res?.status === 200) {
      const rows = res?.data?.data?.data || [];
      setExpenditureRows(Array.isArray(rows) ? rows : []);
    }
    setLoadingExp(false);
  }, []);

  useEffect(() => {
    fetchExpenditureSettings();
  }, [fetchExpenditureSettings]);

  useEffect(() => {
    searchExportInvoices("");
  }, [searchExportInvoices]);

  useEffect(() => {
    searchSalesContracts("");
  }, [searchSalesContracts]);

  useEffect(() => {
    return () => {
      debouncedExportSearch.cancel();
      debouncedScSearch.cancel();
    };
  }, [debouncedExportSearch, debouncedScSearch]);

  useEffect(() => {
    if (!invoiceNumber) {
      setSelectedExport(null);
      return;
    }
    const opt = exportInvoiceOptions.find((o) => o.value === invoiceNumber);
    if (opt?.invoice) {
      setSelectedExport(opt.invoice);
      return;
    }
    setSelectedExport(null);
  }, [invoiceNumber, exportInvoiceOptions]);

  useEffect(() => {
    const invVal = selectedExport?.invoice_value;
    const cr = conversionRate;
    if (invVal == null || cr == null || cr === "") {
      form.setFieldsValue({ agency_commission: undefined });
      return;
    }
    form.setFieldsValue({
      agency_commission: computeExportAgencyCommissionTaka(invVal, cr),
    });
  }, [selectedExport, conversionRate, form]);

  /** Defaults ETA / Berthing / Delivery from the export invoice (exp_date, on_board_date, hand_over_date); user may override. */
  const fillShipmentDatesFromInvoice = useCallback((ex) => {
    form.setFieldsValue({
      eta_date: ex?.exp_date ? moment(ex.exp_date) : null,
      berthing_date: ex?.on_board_date ? moment(ex.on_board_date) : null,
      delivery_date: ex?.hand_over_date ? moment(ex.hand_over_date) : null,
    });
  }, [form]);

  const mergeSalesContractFromExportInvoice = useCallback((ex) => {
    if (!ex) return;
    const rawId = ex.sales_contract_id;
    if (rawId == null || rawId === "") return;

    const scId = Number(rawId);
    const label =
      (ex.sales_contract_number != null &&
        String(ex.sales_contract_number).trim()) ||
      ex.sales_contract_info?.reference_no ||
      ex.sale_contract_info?.reference_no ||
      `SC ${scId}`;

    form.setFieldsValue({ sales_contract_id: scId });

    setSalesContractOptions((prev) => {
      if (prev.some((s) => s.value === scId)) return prev;
      return [{ value: scId, label: String(label) }, ...prev];
    });
  }, [form]);

  const onExportInvoiceChange = (id) => {
    setExportInvoiceSelectSearch(undefined);
    const opt = exportInvoiceOptions.find((o) => o.value === id);
    const ex = opt?.invoice;
    mergeSalesContractFromExportInvoice(ex);
    fillShipmentDatesFromInvoice(ex);
  };

  const mergeBillDetailsFromApi = useCallback((detailsFromApi) => {
    return (detailsFromApi || []).map((d) => ({
      expenditure_id: d.expenditure_id,
      amount:
        d.amount !== undefined && d.amount !== null ? Number(d.amount) : 0,
    }));
  }, []);

  useEffect(() => {
    if (!isEdit || !editInfo) return;

    const invId = editInfo.invoice_number;
    const ex = editInfo.export_invoice || null;

    if (ex) {
      setExportInvoiceOptions((prev) => {
        if (prev.some((p) => p.value === ex.id)) return prev;
        return [
          {
            value: ex.id,
            label: ex.invoice_number || `Invoice #${ex.id}`,
            invoice: ex,
          },
          ...prev,
        ];
      });
      setSelectedExport(ex);
    }

    setDocumentFileList(
      editInfo.document
        ? [
            {
              uid: "-1",
              name: editInfo.document.split("/").pop(),
              status: "done",
              url: `${process.env.REACT_APP_BASE_URL}/${editInfo.document}`,
            },
          ]
        : [],
    );

    const billDetails = mergeBillDetailsFromApi(editInfo.bill_details);

    form.setFieldsValue({
      bill_no: editInfo.bill_no,
      invoice_number: invId,
      sales_contract_id:
        ex?.sales_contract_id != null && ex?.sales_contract_id !== ""
          ? Number(ex.sales_contract_id)
          : undefined,
      eta_date: savedOrInvoiceDate(editInfo.eta_date, ex?.exp_date),
      berthing_date: savedOrInvoiceDate(editInfo.berthing_date, ex?.on_board_date),
      delivery_date: savedOrInvoiceDate(editInfo.delivery_date, ex?.hand_over_date),
      goods_received_date: editInfo.goods_received_date
        ? moment(editInfo.goods_received_date)
        : null,
      goods_hand_over_date: editInfo.goods_hand_over_date
        ? moment(editInfo.goods_hand_over_date)
        : null,
      documents_received_date: editInfo.documents_received_date
        ? moment(editInfo.documents_received_date)
        : null,
      conversion_rate: editInfo.conversion_rate
        ? Number(editInfo.conversion_rate)
        : undefined,
      agency_commission: editInfo.agency_commission
        ? Number(editInfo.agency_commission)
        : undefined,
      bill_details:
        billDetails.length > 0
          ? billDetails
          : [{ expenditure_id: undefined, amount: undefined }],
    });

    if (ex) {
      mergeSalesContractFromExportInvoice(ex);
    }
  }, [isEdit, editInfo, form, mergeBillDetailsFromApi, mergeSalesContractFromExportInvoice]);

  useEffect(() => {
    if (isEdit) return;
    form.setFieldsValue({
      bill_details: [{ expenditure_id: undefined, amount: undefined }],
    });

    const fetchNextBillNo = async () => {
      const res = await getData(EXPORT_CNF_BILL_NEXT_BILL_NO_ENDPOINT);
      if (res?.status === 200) {
        form.setFieldsValue({ bill_no: res?.data?.data?.bill_no });
      }
    };
    fetchNextBillNo();
  }, [isEdit, form]);

  const display = useMemo(() => {
    const ex = selectedExport;
    if (!ex) {
      return {
        invoiceValue: "",
        qty: "",
        lc: "",
        goods: "",
        boeNumber: "",
        boeDate: "",
      };
    }
    return {
      invoiceValue: ex.invoice_value ?? "",
      qty: ex.total_quantity ?? "",
      lc: ex.sc_commercial_lc_value ?? ex.sc_commercial_lc_id ?? "",
      goods: ex.goods_description_str ?? "",
      boeNumber: ex.boe_number ?? "",
      boeDate: formatDisplayDate(ex.boe_date),
    };
  }, [selectedExport]);

  const billCreateDate = formatDisplayDate(
    isEdit && editInfo?.created_at ? editInfo.created_at : moment(),
  );

  const expenditureOptions = useMemo(
    () => buildExpenditureOptions(expenditureRows),
    [expenditureRows],
  );

  const onFinish = async (values) => {
    const bill_details = (values.bill_details || [])
      .filter(
        (row) =>
          row &&
          row.expenditure_id != null &&
          row.amount !== undefined &&
          row.amount !== null,
      )
      .map((row) => ({
        expenditure_id: row.expenditure_id,
        amount: Number(row.amount) || 0,
      }));

    if (!bill_details.length) {
      notification.error({
        message: "Validation",
        description: "Add at least one expenditure row with type and amount.",
        placement: "topRight",
      });
      return;
    }

    const agencyCommission =
      values.agency_commission ??
      computeExportAgencyCommissionTaka(
        selectedExport?.invoice_value,
        values.conversion_rate,
      );

    const formatDate = (value) => (value ? value.format("YYYY-MM-DD") : "");

    const payload = new FormData();
    payload.append("invoice_number", values.invoice_number);
    payload.append("eta_date", formatDate(values.eta_date));
    payload.append("berthing_date", formatDate(values.berthing_date));
    payload.append("delivery_date", formatDate(values.delivery_date));
    payload.append("goods_received_date", formatDate(values.goods_received_date));
    payload.append("goods_hand_over_date", formatDate(values.goods_hand_over_date));
    payload.append(
      "documents_received_date",
      formatDate(values.documents_received_date),
    );
    payload.append("conversion_rate", values.conversion_rate ?? "");
    payload.append("agency_commission", agencyCommission ?? "");
    payload.append("bill_details", JSON.stringify(bill_details));
    if (documentFileList[0]?.originFileObj) {
      payload.append("document", documentFileList[0].originFileObj);
    }

    if (isEdit && billId) {
      // PHP does not parse multipart bodies on PUT, so spoof the method over POST.
      payload.append("_method", "PUT");
      const res = await postData(
        `${EXPORT_CNF_BILL_EDIT_ENDPOINT}/${billId}`,
        payload,
      );
      if (res && (res?.code === 200 || res?.status === 200)) {
        notification.success({
          message: "Success",
          description: res?.message || "Updated",
          placement: "topRight",
        });
        navigate("/import-export-expense-tracking/external-cnf-bills/export-cnf-bill");
      }
    } else {
      const res = await postData(EXPORT_CNF_BILL_STORE_ENDPOINT, payload);
      if (res && (res?.code === 200 || res?.status === 200)) {
        notification.success({
          message: "Success",
          description: res?.message || "Created",
          placement: "topRight",
        });
        navigate("/import-export-expense-tracking/external-cnf-bills/export-cnf-bill");
      }
    }
  };

  const disabled = !!viewMode;

  return (
    <Card loading={loadingExp}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16}>
          <Col xs={24} md={4}>
            <Form.Item name="bill_no" label="Bill Number">
              <Input readOnly disabled placeholder="Loading..." />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="Bill Create Date">
              <Input readOnly disabled value={billCreateDate} />
            </Form.Item>
          </Col>
        </Row>

        <Text strong>Commercial invoice</Text>
        <Row gutter={16}>
          <Col xs={24} md={4}>
            <Form.Item
              name="invoice_number"
              label="Commercial Invoice Number"
              tooltip="All Export Invoices"
              rules={[{ required: true, message: "Required" }]}
            >
              <Select
                showSearch
                placeholder="All Export Invoices — type to search"
                loading={loadingInvSearch}
                disabled={disabled}
                filterOption={false}
                defaultActiveFirstOption={false}
                optionFilterProp="label"
                searchValue={exportInvoiceSelectSearch}
                onSearch={(text) => {
                  setExportInvoiceSelectSearch(text);
                  debouncedExportSearch(text);
                }}
                onChange={onExportInvoiceChange}
                allowClear
                onClear={() => {
                  setExportInvoiceSelectSearch(undefined);
                  setSelectedExport(null);
                  form.setFieldsValue({
                    sales_contract_id: undefined,
                    eta_date: null,
                    berthing_date: null,
                    delivery_date: null,
                  });
                }}
                options={exportInvoiceOptions.map(({ value, label }) => ({
                  value,
                  label,
                }))}
                onDropdownVisibleChange={(open) => {
                  if (open && exportInvoiceOptions.length === 0) {
                    searchExportInvoices("");
                  }
                  if (!open) {
                    setExportInvoiceSelectSearch(undefined);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              name="sales_contract_id"
              label="Sales Contract Number"
              // tooltip="Search sales contracts (not stored on bill API)"
            >
              <Select
                showSearch
                placeholder="Search by reference no."
                loading={loadingScSearch}
                disabled={disabled}
                filterOption={false}
                onSearch={(t) => debouncedScSearch(t)}
                options={salesContractOptions}
                allowClear
                onDropdownVisibleChange={(open) => {
                  if (open && salesContractOptions.length === 0) {
                    searchSalesContracts("");
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="Invoice Value">
              <Input disabled value={display.invoiceValue} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="Invoice Quantity">
              <Input disabled value={display.qty} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="LC Number / value">
              <Input disabled value={display.lc} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item name="eta_date" label="ETA Date">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={disabled}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={4}>
            <Form.Item name="berthing_date" label="Berthing Date">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={disabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item name="delivery_date" label="Delivery Date">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={disabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              label="Description of Goods"
              // tooltip="Auto (Non Editable) — from export invoice"
            >
              <Input readOnly disabled value={display.goods} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              label="Bill of Export Number"
              tooltip="Auto (Non Editable) — from export invoice"
            >
              <Input readOnly disabled value={display.boeNumber} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              label="Bill of Export Date"
              tooltip="Auto (Non Editable) — from export invoice"
            >
              <Input readOnly disabled value={display.boeDate} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item name="goods_received_date" label="Goods Received Date">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={disabled}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={4}>
            <Form.Item name="goods_hand_over_date" label="Goods Hand Over Date">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={disabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              name="documents_received_date"
              label="Documents Received Date"
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={disabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              name="conversion_rate"
              label="Conversion Rate"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                disabled={disabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item name="agency_commission" label="Agency Commission (Taka)">
              <InputNumber style={{ width: "100%" }} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Document (PDF)">
              <Upload
                accept=".pdf,application/pdf"
                maxCount={1}
                fileList={documentFileList}
                disabled={disabled}
                showUploadList={{ showRemoveIcon: !disabled }}
                beforeUpload={(file) => {
                  const isPdf =
                    file.type === "application/pdf" ||
                    file.name?.toLowerCase().endsWith(".pdf");
                  if (!isPdf) {
                    notification.error({
                      message: "Invalid file",
                      description: "Only PDF files are allowed.",
                      placement: "topRight",
                    });
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                onChange={({ fileList }) => setDocumentFileList(fileList.slice(-1))}
              >
                {!disabled && (
                  <Button icon={<UploadOutlined />}>
                    {documentFileList.length > 0 ? "Change PDF" : "Upload PDF"}
                  </Button>
                )}
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Particulars of Expenditure (Export C&F settings)
        </Text>
        <ExpenditureParticularsTable
          expenditureOptions={expenditureOptions}
          disabled={disabled}
          loadingOptions={loadingExp}
        />
      </Form>
    </Card>
  );
}
